import React, { memo, useEffect, useRef } from "react";
import { getClassName } from "@sscs/utils";
import { ChevronRightIcon as ArrowRight } from "../../icons";
import Tooltip from "../Tooltip/Tooltip";
import styles from "./treeView.module.scss";
const TreeRow = memo(function TreeRow({ row, selected, selectedParentId, focusedId, pendingFocusId, compact, separated, onSelect, onToggle, onExpand, onFocusRow, onPendingFocusHandled, }) {
    const { id, node, depth, hasChildren, expanded, parentId, ancestors } = row;
    const { label, shortLabel, menu, count, tooltip, expandableOnly = false, disabled = false, } = node;
    const rowRef = useRef(null);
    const labelRef = useRef(null);
    const isSelected = !expandableOnly && selected === id;
    useEffect(() => {
        var _a;
        const labelEl = labelRef.current;
        if (!labelEl)
            return;
        labelEl.querySelectorAll("a, button").forEach((element) => {
            element.tabIndex = -1;
        });
        if (compact && labelEl.scrollWidth > labelEl.clientWidth) {
            labelEl.title = (_a = labelEl.textContent) !== null && _a !== void 0 ? _a : "";
        }
        else {
            labelEl.removeAttribute("title");
        }
    });
    useEffect(() => {
        if (pendingFocusId === id && rowRef.current) {
            rowRef.current.focus();
            onPendingFocusHandled();
        }
    }, [pendingFocusId, id, onPendingFocusHandled]);
    const handleChevronClick = (event) => {
        event.stopPropagation();
        onToggle(id);
    };
    const handleRowClick = (event) => {
        var _a;
        if (event.target instanceof Node &&
            !event.currentTarget.contains(event.target))
            return;
        if (disabled)
            return;
        if (event.target.closest("[data-tree-menu]"))
            return;
        if (!expandableOnly)
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(id);
        if (hasChildren) {
            if (expandableOnly)
                onToggle(id);
            else
                onExpand(id);
        }
        const action = (_a = labelRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("a, button");
        if (action && !action.contains(event.target))
            action.click();
    };
    const innerClasses = getClassName(styles.inner, {
        [styles["inner-selected"]]: isSelected,
        [styles["inner-parent"]]: hasChildren,
        [styles["inner-expandable-only"]]: expandableOnly,
        [styles["inner-disabled"]]: disabled,
    });
    const isLastChild = row.posInSet === row.setSize;
    const ownGuideSelected = parentId !== null && parentId === selectedParentId;
    const stubSelected = selected === id;
    const stemSelected = selectedParentId !== null && id === selectedParentId;
    const treeItem = (React.createElement("div", { ref: rowRef, role: "treeitem", "data-node-id": id, "aria-level": depth + 1, "aria-setsize": row.setSize, "aria-posinset": row.posInSet, "aria-selected": expandableOnly ? undefined : isSelected, "aria-expanded": hasChildren ? expanded : undefined, "aria-disabled": disabled || undefined, tabIndex: focusedId === id ? 0 : -1, onFocus: () => onFocusRow(id), className: getClassName(styles.row, {
            [styles["tree-separated"]]: separated,
        }) },
        Array.from({ length: depth }, (_unused, level) => {
            if (level === depth - 1) {
                return (React.createElement("span", { key: level, "aria-hidden": true, className: getClassName(styles.rail, styles["rail-guide"], {
                        [styles["rail-guide-last"]]: isLastChild,
                        [styles["rail-guide-selected"]]: ownGuideSelected,
                        [styles["rail-stub-selected"]]: stubSelected,
                    }) }));
            }
            const ancestor = ancestors[level];
            return (React.createElement("span", { key: level, "aria-hidden": true, className: getClassName(styles.rail, {
                    [styles["rail-line"]]: ancestor.continues,
                    [styles["rail-line-selected"]]: ancestor.continues && ancestor.groupId === selectedParentId,
                }) }));
        }),
        hasChildren && expanded && (React.createElement("span", { "aria-hidden": true, className: getClassName(styles.stem, {
                [styles["stem-selected"]]: stemSelected,
            }) })),
        React.createElement("div", { onClick: handleRowClick, className: innerClasses },
            hasChildren && (React.createElement("span", { "data-tree-chevron": true, "aria-hidden": true, onClick: handleChevronClick, className: getClassName(styles["expand-icon"], {
                    [styles["expand-icon-expanded"]]: expanded,
                }) },
                React.createElement(ArrowRight, { size: "xs", className: styles.chevron }))),
            React.createElement("div", { className: styles.label },
                React.createElement("div", { ref: labelRef, "data-tree-label": true, className: getClassName(styles.button, {
                        [styles["button-compact"]]: compact,
                    }) }, compact ? (shortLabel !== null && shortLabel !== void 0 ? shortLabel : label) : label),
                !compact && count !== null && count !== undefined && (React.createElement("span", { className: getClassName(styles.count, {
                        [styles["count-value"]]: typeof count === "number" && count > 0,
                        [styles["count-zero"]]: count === 0,
                    }) }, typeof count === "number" ? count.toLocaleString() : count)),
                !compact && menu && (React.createElement("span", { "data-tree-menu": true, className: styles.menu }, menu))))));
    if (tooltip === undefined || tooltip === null)
        return treeItem;
    return (React.createElement(Tooltip, { title: tooltip, placement: "right", describeChild: true }, treeItem));
});
export default TreeRow;
//# sourceMappingURL=TreeRow.js.map